import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { pickFace, applyView, VIEW_DIRS, viewsFromFrontBottom, enableViewCube, tiltView, renderViewCubeGizmo, snapPoint, circleFromFace, showSnapMarker, hideSnapMarker, showDimPreview, clearDimPreview, renderExtras, offsetFromCursor } from "@/lib/threeManual";
import { X, Upload, Camera, Move3d, Loader2, Hash, Type, Layers, Shapes, RotateCw, Plus, Trash2, Eye, EyeOff, Maximize, SquareDashedMousePointer, Home, ChevronUp, ChevronDown, Ruler } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const ICON_PATHS = {
  "arrow-right": '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  "arrow-left": '<path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>',
  "arrow-up": '<path d="M12 19V5"/><path d="m5 12 7-7 7 7"/>',
  "arrow-down": '<path d="M12 5v14"/><path d="m19 12-7 7-7-7"/>',
  "arrow-diagonal": '<path d="M7 17 17 7"/><path d="M7 7h10v10"/>',
  "plus": '<path d="M12 5v14"/><path d="M5 12h14"/>',
  "close": '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  "check": '<path d="M20 6 9 17l-5-5"/>',
  "alert": '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  "circle": '<circle cx="12" cy="12" r="9"/>',
};
const iconSvg = (key) => `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='#1b1b1b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>${ICON_PATHS[key]}</svg>`;
const iconUri = (key) => "data:image/svg+xml," + encodeURIComponent(iconSvg(key));

export default function ThreeCaptureModal({ onClose, onCapture }) {
  const mountRef = useRef(null);
  const engine = useRef(null);
  const fileRef = useRef(null);
  const showFacesRef = useRef(true);
  const edgeAngleRef = useRef(45);
  const [loading, setLoading] = useState(false);
  const [hasModel, setHasModel] = useState(false);
  const [error, setError] = useState("");
  const [showFaces, setShowFaces] = useState(true);
  const [edgeAngle, setEdgeAngle] = useState(45);
  const [assemblyRadius, setAssemblyRadius] = useState(50);
  const [parts, setParts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [iconPalette, setIconPalette] = useState(false);
  const [faceMode, setFaceMode] = useState(false);
  const [viewNormals, setViewNormals] = useState({});
  const [frontNormal, setFrontNormal] = useState(null);
  const highlightRef = useRef([]);
  const downPos = useRef(null);
  const dimP1 = useRef(null);
  const dimP2 = useRef(null);
  const lastOffset = useRef(null);
  const dim3dModeRef = useRef(null);
  const [dim3dMode, setDim3dMode] = useState(null); // 'aligned'|'x'|'y'|'z'|'dia'|null
  const [dims3d, setDims3d] = useState([]);

  useEffect(() => {
    const mount = mountRef.current;
    const w = mount.clientWidth, h = mount.clientHeight;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000000);
    camera.position.set(120, 90, 160);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    let raf;
    const animate = () => { raf = requestAnimationFrame(animate); controls.update(); renderer.render(scene, camera); if (engine.current?._gizmo) renderViewCubeGizmo(engine.current); };
    animate();
    const onResize = () => { const nw = mount.clientWidth, nh = mount.clientHeight; renderer.setSize(nw, nh); camera.aspect = nw / nh; camera.updateProjectionMatrix(); };
    window.addEventListener("resize", onResize);
    engine.current = { renderer, scene, camera, controls, parts: [], center: new THREE.Vector3(), radius: 50, mount, _extras: [] };
    enableViewCube(engine.current, (name) => setView(name));
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      controls.dispose(); renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      engine.current = null;
    };
  }, []);

  const buildTechnical = (root, faces, angle, color) => {
    root.updateMatrixWorld(true);
    const group = new THREE.Group();
    root.traverse((child) => {
      if (child.isMesh && child.geometry) {
        const g = child.geometry.clone().applyMatrix4(child.matrixWorld);
        const faceMat = color ? new THREE.MeshBasicMaterial({ color: new THREE.Color(color) }) : faces ? new THREE.MeshBasicMaterial({ color: 0xdedede }) : new THREE.MeshBasicMaterial({ colorWrite: false });
        faceMat.polygonOffset = true; faceMat.polygonOffsetFactor = 1; faceMat.polygonOffsetUnits = 1;
        group.add(new THREE.Mesh(g, faceMat));
        group.add(new THREE.LineSegments(new THREE.EdgesGeometry(g, angle), new THREE.LineBasicMaterial({ color: 0x1b1b1b })));
        let og = g.clone();
        try { og = mergeVertices(og); } catch (e) { /* keep */ }
        og.computeVertexNormals(); og.computeBoundingSphere();
        const t = (og.boundingSphere?.radius || 1) * 0.008;
        const opos = og.attributes.position, onor = og.attributes.normal;
        for (let i = 0; i < opos.count; i++) opos.setXYZ(i, opos.getX(i) + onor.getX(i) * t, opos.getY(i) + onor.getY(i) * t, opos.getZ(i) + onor.getZ(i) * t);
        opos.needsUpdate = true;
        group.add(new THREE.Mesh(og, new THREE.MeshBasicMaterial({ color: 0x1b1b1b, side: THREE.BackSide })));
      }
    });
    return group;
  };

  const makeInner = (root, faces, angle, color) => {
    const inner = buildTechnical(root, faces, angle, color);
    const box = new THREE.Box3().setFromObject(inner);
    const c = box.getCenter(new THREE.Vector3());
    inner.position.sub(c);
    return inner;
  };

  const fitAll = () => {
    const e = engine.current; if (!e || !e.parts.length) return;
    const box = new THREE.Box3();
    e.parts.forEach((p) => box.expandByObject(p.outer));
    if (box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) || 1;
    e.center = center; e.radius = radius; e._box = box.clone();
    setAssemblyRadius(radius);
    const d = radius * 2.2;
    e.camera.position.set(center.x + d * 0.7, center.y + d * 0.55, center.z + d);
    e.camera.near = radius / 1000; e.camera.far = radius * 1000; e.camera.updateProjectionMatrix();
    e.controls.target.copy(center); e.controls.update();
  };

  useEffect(() => {
    showFacesRef.current = showFaces; edgeAngleRef.current = edgeAngle;
    const e = engine.current;
    if (e?.parts?.length) e.parts.forEach((p) => { p.outer.remove(p.inner); p.inner = makeInner(p.sourceRoot, showFaces, edgeAngle, p.appliedColor); p.outer.add(p.inner); });
  }, [showFaces, edgeAngle]);

  useEffect(() => {
    const e = engine.current; if (!e) return;
    parts.forEach((meta) => {
      const p = e.parts.find((x) => x.id === meta.id); if (!p) return;
      if (p.appliedColor !== (meta.color || null)) {
        p.outer.remove(p.inner);
        p.inner = makeInner(p.sourceRoot, showFacesRef.current, edgeAngleRef.current, meta.color || null);
        p.outer.add(p.inner);
        p.appliedColor = meta.color || null;
      }
      p.outer.position.set(meta.px, meta.py, meta.pz);
      p.outer.rotation.set(THREE.MathUtils.degToRad(meta.rx), THREE.MathUtils.degToRad(meta.ry), THREE.MathUtils.degToRad(meta.rz));
      p.outer.visible = meta.visible;
    });
  }, [parts]);

  const addPart = (root, name) => {
    const e = engine.current;
    const id = uid();
    const outer = new THREE.Group();
    const inner = makeInner(root, showFacesRef.current, edgeAngleRef.current, null);
    outer.add(inner);
    e.scene.add(outer);
    e.parts.push({ id, sourceRoot: root, outer, inner, appliedColor: null });
    setParts((prev) => [...prev, { id, name: name || `Onderdeel ${prev.length + 1}`, px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0, visible: true, color: null }]);
    setSelectedIds([id]);
    setHasModel(true); setLoading(false);
    fitAll();
  };

  const removePart = (id) => {
    const e = engine.current;
    const idx = e.parts.findIndex((p) => p.id === id);
    if (idx >= 0) { e.scene.remove(e.parts[idx].outer); e.parts.splice(idx, 1); }
    setParts((prev) => prev.filter((p) => p.id !== id));
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    if (!e.parts.length) setHasModel(false); else fitAll();
  };

  const updatePart = (id, patch) => setParts((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const selectPart = (id, ev) => {
    const multi = ev && (ev.ctrlKey || ev.metaKey || ev.shiftKey);
    setSelectedIds((prev) => (multi ? (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]) : [id]));
  };
  const applyAxis = (axis, newVal) => {
    const primary = parts.find((p) => p.id === selectedIds[0]); if (!primary) return;
    const delta = newVal - primary[axis];
    setParts((prev) => prev.map((p) => (selectedIds.includes(p.id) ? { ...p, [axis]: p[axis] + delta } : p)));
  };
  const setColorSelected = (color) => setParts((prev) => prev.map((p) => (selectedIds.includes(p.id) ? { ...p, color } : p)));
  const resetSelected = () => setParts((prev) => prev.map((p) => (selectedIds.includes(p.id) ? { ...p, px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0 } : p)));

  const setView = (dir) => {
    const e = engine.current; if (!e || !e.radius) return;
    applyView(e, viewNormals[dir] || VIEW_DIRS[dir] || VIEW_DIRS.iso);
  };

  const clearFace = () => {
    const e = engine.current;
    if (e) highlightRef.current.forEach((h) => e.scene.remove(h));
    highlightRef.current = []; setFrontNormal(null);
  };
  const onFaceClick = (ev) => {
    if (!faceMode) return;
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return; // ignore drag (orbit)
    const e = engine.current;
    const f = pickFace(e, ev);
    if (!f) { setError("Klik op een vlak van het model"); return; }
    setError("");
    e.scene.add(f.highlight); highlightRef.current.push(f.highlight);
    if (!frontNormal) {
      setFrontNormal(f.normal);
      applyView(e, f.normal);
    } else {
      const views = viewsFromFrontBottom(frontNormal, f.normal);
      setViewNormals(views);
      applyView(e, views.front);
      clearFace(); setFaceMode(false);
    }
  };

  // --- CAD 3D dimensions (snap, black, drag-to-place) — appear in the captured image ---
  const startDim3d = (kind) => {
    const e = engine.current; if (!e || !hasModel) return;
    if (dim3dMode === kind) { setDim3dMode(null); return; }
    setFaceMode(false); clearFace(); setError("");
    dimP1.current = null; dimP2.current = null; lastOffset.current = null; clearDimPreview(e); hideSnapMarker(e);
    setDim3dMode(kind);
    setError(kind === "dia" ? "Klik op een rond vlak" : "Klik 2 snappunten, sleep dan de maatlijn en klik om te plaatsen");
  };
  const handleDim3dPick = (ev) => {
    const e = engine.current; const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return;
    const kind = dim3dModeRef.current; if (!kind) return;
    if (kind === "dia") {
      const circ = circleFromFace(e, ev);
      if (!circ || !circ.radius) { setError("Klik op een rond vlak (cirkel/boog)"); return; }
      setDims3d((arr) => [...arr, { id: uid(), kind: "dia", center: circ.center, radius: circ.radius, normal: circ.normal }]);
      hideSnapMarker(e); setDim3dMode(null); setError(""); return;
    }
    if (dimP1.current && dimP2.current) {
      const d = { id: uid(), kind, p1: dimP1.current, p2: dimP2.current, offset: lastOffset.current };
      dimP1.current = null; dimP2.current = null; lastOffset.current = null;
      setDims3d((arr) => [...arr, d]);
      hideSnapMarker(e); clearDimPreview(e); setDim3dMode(null); setError("");
      return;
    }
    const sn = snapPoint(e, ev);
    if (!sn) { setError("Klik op het model bij een snappunt"); return; }
    setError("");
    if (!dimP1.current) { dimP1.current = sn.point; return; }
    dimP2.current = sn.point;
  };
  const onDim3dMove = (ev) => {
    const e = engine.current; const kind = dim3dModeRef.current; if (!e || !kind) return;
    if (kind === "dia") { const c = circleFromFace(e, ev); if (c && c.radius) showSnapMarker(e, c.center); else hideSnapMarker(e); return; }
    if (dimP1.current && dimP2.current) {
      const off = offsetFromCursor(e, ev, dimP1.current, dimP2.current, kind);
      lastOffset.current = off; hideSnapMarker(e);
      showDimPreview(e, { kind, p1: dimP1.current, p2: dimP2.current, offset: off });
    } else {
      const sn = snapPoint(e, ev);
      if (sn) { showSnapMarker(e, sn.point); if (dimP1.current) showDimPreview(e, { kind, p1: dimP1.current, p2: sn.point }); }
      else { hideSnapMarker(e); clearDimPreview(e); }
    }
  };
  const removeDim3d = (id) => setDims3d((arr) => arr.filter((d) => d.id !== id));
  useEffect(() => { dim3dModeRef.current = dim3dMode; const e = engine.current; if (e && !dim3dMode) { dimP1.current = null; dimP2.current = null; lastOffset.current = null; clearDimPreview(e); hideSnapMarker(e); } }, [dim3dMode]);
  useEffect(() => { const e = engine.current; if (!e || !hasModel) return; renderExtras(e, dims3d); }, [dims3d, hasModel]);

  const loadFile = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["stl", "obj", "glb", "gltf"].includes(ext)) { setError("Niet-ondersteund formaat. Exporteer als STL of OBJ."); return; }
    const base = f.name.replace(/\.[^.]+$/, "");
    setError(""); setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        if (ext === "stl") addPart(new THREE.Mesh(new STLLoader().parse(reader.result), new THREE.MeshBasicMaterial()), base);
        else if (ext === "obj") addPart(new OBJLoader().parse(reader.result), base);
        else new GLTFLoader().parse(reader.result, "", (gltf) => addPart(gltf.scene, base), () => { setError("Kon GLB/GLTF niet laden."); setLoading(false); });
      } catch { setError("Kon het model niet laden. Probeer STL of OBJ."); setLoading(false); }
    };
    if (ext === "obj") reader.readAsText(f); else reader.readAsArrayBuffer(f);
  };

  const onUpload = (e) => { const files = Array.from(e.target.files || []); e.target.value = ""; files.forEach(loadFile); };

  // annotations
  const addNumber = () => { const n = annotations.filter((a) => a.type === "number").length + 1; const id = uid(); setAnnotations((a) => [...a, { id, type: "number", n, xr: 0.5, yr: 0.5 }]); setSelectedId(id); };
  const addText = () => { const id = uid(); setAnnotations((a) => [...a, { id, type: "text", text: "Tekst", xr: 0.4, yr: 0.5 }]); setSelectedId(id); };
  const addIcon = (icon) => { const id = uid(); setAnnotations((a) => [...a, { id, type: "icon", icon, rot: 0, xr: 0.5, yr: 0.5 }]); setSelectedId(id); setIconPalette(false); };
  const updateAnn = (id, patch) => setAnnotations((a) => a.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeAnn = (id) => { setAnnotations((a) => a.filter((x) => x.id !== id)); setSelectedId(null); };

  const startDrag = (ev, id) => {
    ev.stopPropagation(); setSelectedId(id);
    const onMove = (e2) => { const r = mountRef.current.getBoundingClientRect(); updateAnn(id, { xr: Math.min(1, Math.max(0, (e2.clientX - r.left) / r.width)), yr: Math.min(1, Math.max(0, (e2.clientY - r.top) / r.height)) }); };
    const onUp = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    window.addEventListener("pointermove", onMove); window.addEventListener("pointerup", onUp);
  };

  const capture = async () => {
    const e0 = engine.current; if (e0) { hideSnapMarker(e0); clearDimPreview(e0); }
    const { renderer, scene, camera } = engine.current;
    const mount = mountRef.current;
    const aspect = camera.aspect, targetLong = 2400;
    const ew = aspect >= 1 ? targetLong : Math.round(targetLong * aspect);
    const eh = aspect >= 1 ? Math.round(targetLong / aspect) : targetLong;
    const prevPR = renderer.getPixelRatio();
    renderer.setPixelRatio(1); renderer.setSize(ew, eh, false); renderer.render(scene, camera);
    const modelUrl = renderer.domElement.toDataURL("image/png");
    renderer.setPixelRatio(prevPR); renderer.setSize(mount.clientWidth, mount.clientHeight);
    camera.aspect = mount.clientWidth / mount.clientHeight; camera.updateProjectionMatrix();

    const out = document.createElement("canvas"); out.width = ew; out.height = eh;
    const ctx = out.getContext("2d");
    const base = new Image(); await new Promise((res) => { base.onload = res; base.src = modelUrl; });
    ctx.drawImage(base, 0, 0, ew, eh);
    const iconImgs = {};
    await Promise.all([...new Set(annotations.filter((a) => a.type === "icon").map((a) => a.icon))].map((key) => new Promise((res) => { const im = new Image(); im.onload = () => { iconImgs[key] = im; res(); }; im.onerror = res; im.src = iconUri(key); })));
    const r = Math.round(ew * 0.016);
    annotations.forEach((a) => {
      const x = a.xr * ew, y = a.yr * eh;
      if (a.type === "number") {
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = "#1b1b1b"; ctx.fill();
        ctx.lineWidth = Math.max(1, r * 0.14); ctx.strokeStyle = "#DB960D"; ctx.stroke();
        ctx.fillStyle = "#fff"; ctx.font = `700 ${Math.round(r * 1.25)}px Outfit, Arial, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(String(a.n), x, y + r * 0.05);
      } else if (a.type === "text") {
        const fs = Math.round(ew * 0.02);
        ctx.font = `600 ${fs}px Manrope, Arial, sans-serif`; ctx.textAlign = "left"; ctx.textBaseline = "top";
        const pad = fs * 0.4, tw = ctx.measureText(a.text || "").width;
        ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.fillRect(x - pad, y - pad, tw + pad * 2, fs + pad * 2);
        ctx.fillStyle = "#1b1b1b"; ctx.fillText(a.text || "", x, y);
      } else if (a.type === "icon") {
        const im = iconImgs[a.icon];
        if (im) { const s = ew * 0.06; ctx.save(); ctx.translate(x, y); ctx.rotate(((a.rot || 0) * Math.PI) / 180); ctx.drawImage(im, -s / 2, -s / 2, s, s); ctx.restore(); }
      }
    });
    onCapture(out.toDataURL("image/png"));
    onClose();
  };

  const selected = annotations.find((a) => a.id === selectedId);
  const selPart = parts.find((p) => p.id === selectedIds[0]);
  const iconKeys = Object.keys(ICON_PATHS);
  const posMax = assemblyRadius * 2, posStep = Math.max(0.01, assemblyRadius / 200);

  const fmtVal = (v, step, unit) => `${Number(v).toFixed(step >= 1 ? 0 : 1)}${unit}`;
  const sliderRow = (label, value, min, max, step, onChange, testid, unit = "") => (
    <div className="group flex items-center gap-2">
      <span className="text-[11px] w-4 text-kann-text-sec">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} title={fmtVal(value, step, unit)} className="flex-1 accent-kann-gold" />
      <span className="text-[10px] tabular-nums w-11 text-right text-kann-text-sec opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testid}-value`}>{fmtVal(value, step, unit)}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1000] bg-kann-base flex flex-col" data-testid="three-capture-modal">
      <div className="bg-kann-surface w-full h-full flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-kann-border/20 shrink-0">
          <Move3d className="w-5 h-5 text-kann-gold" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-kann-text-pri">3D → Technische tekening</div>
            <div className="text-xs text-kann-text-sec">Laad meerdere onderdelen, stel ze samen tot een exploded view, en leg vast.</div>
          </div>
          <button onClick={onClose} data-testid="three-close-btn" className="p-2 rounded-md hover:bg-kann-surface-active text-kann-text-sec"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Parts panel */}
          <div className="w-64 shrink-0 border-r border-kann-border/20 flex flex-col">
            <div className="p-3 border-b border-kann-border/20 flex gap-2">
              <button onClick={() => fileRef.current?.click()} data-testid="add-part-btn" className="flex-1 bg-kann-ink text-white text-sm font-semibold rounded-full px-3 py-2 hover:bg-black flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Onderdeel</button>
              <button onClick={fitAll} disabled={!hasModel} data-testid="fit-btn" title="Passend maken" className="border border-kann-border/50 rounded-full px-3 py-2 text-kann-text-sec hover:bg-kann-surface-active disabled:opacity-40"><Maximize className="w-4 h-4" /></button>
              <input ref={fileRef} type="file" multiple accept=".stl,.obj,.glb,.gltf" className="hidden" onChange={onUpload} data-testid="part-file-input" />
            </div>
            {parts.length > 0 && <div className="px-3 py-1.5 text-[10px] text-kann-text-sec border-b border-kann-border/10">Ctrl/Shift + klik om meerdere te selecteren</div>}
            <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="parts-list">
              {parts.length === 0 && <div className="text-xs text-kann-text-sec p-3">Nog geen onderdelen. Klik op "Onderdeel".</div>}
              {parts.map((p) => (
                <div key={p.id} onClick={(ev) => selectPart(p.id, ev)} data-testid={`part-row-${p.id}`}
                  className={`rounded-md px-2 py-1.5 cursor-pointer flex items-center gap-1.5 ${selectedIds.includes(p.id) ? "bg-kann-gold/15 ring-1 ring-kann-gold" : "hover:bg-kann-surface-active"}`}>
                  <span className="w-3 h-3 rounded-sm shrink-0 border border-kann-border/40" style={{ background: p.color || "#dedede" }} />
                  <input value={p.name} onChange={(e) => updatePart(p.id, { name: e.target.value })} onClick={(e) => { e.stopPropagation(); selectPart(p.id, e); }} data-testid={`part-name-${p.id}`} className="flex-1 min-w-0 bg-transparent text-xs text-kann-text-pri outline-none" />
                  <button onClick={(e) => { e.stopPropagation(); updatePart(p.id, { visible: !p.visible }); }} data-testid={`part-visible-${p.id}`} className="text-kann-text-sec hover:text-kann-text-pri">{p.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>
                  <button onClick={(e) => { e.stopPropagation(); removePart(p.id); }} data-testid={`part-delete-${p.id}`} className="text-kann-text-sec hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            {selPart && (
              <div className="p-3 border-t border-kann-border/20 space-y-2" data-testid="part-controls">
                <div className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono">
                  {selectedIds.length > 1 ? `${selectedIds.length} onderdelen samen` : `Positie (${selPart.name})`}
                </div>
                <div className="flex items-center gap-1.5 pb-1" data-testid="part-color-row">
                  <span className="text-[11px] text-kann-text-sec mr-0.5">Kleur</span>
                  {["#dedede", "#DB960D", "#232323", "#C0392B", "#2E86DE", "#27AE60"].map((c) => (
                    <button key={c} onClick={() => setColorSelected(c === "#dedede" ? null : c)} data-testid={`part-color-${c}`} title={c === "#dedede" ? "Grijs (standaard)" : c} className={`w-5 h-5 rounded-sm border ${(selPart.color || "#dedede").toLowerCase() === c.toLowerCase() ? "ring-2 ring-kann-gold border-kann-gold" : "border-kann-border/50"}`} style={{ background: c }} />
                  ))}
                  <input type="color" value={selPart.color || "#dedede"} onChange={(e) => setColorSelected(e.target.value)} data-testid="part-color-custom" className="w-6 h-6 p-0 border border-kann-border/50 rounded-sm bg-transparent cursor-pointer" title="Aangepaste kleur" />
                </div>
                {sliderRow("X", selPart.px, -posMax, posMax, posStep, (v) => applyAxis("px", v), "part-pos-x")}
                {sliderRow("Y", selPart.py, -posMax, posMax, posStep, (v) => applyAxis("py", v), "part-pos-y")}
                {sliderRow("Z", selPart.pz, -posMax, posMax, posStep, (v) => applyAxis("pz", v), "part-pos-z")}
                <div className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono pt-1">Rotatie</div>
                {sliderRow("X", selPart.rx, -180, 180, 1, (v) => applyAxis("rx", v), "part-rot-x", "°")}
                {sliderRow("Y", selPart.ry, -180, 180, 1, (v) => applyAxis("ry", v), "part-rot-y", "°")}
                {sliderRow("Z", selPart.rz, -180, 180, 1, (v) => applyAxis("rz", v), "part-rot-z", "°")}
                <button onClick={resetSelected} data-testid="part-reset" className="text-xs text-kann-text-sec hover:text-kann-gold">Reset positie</button>
              </div>
            )}
          </div>

          {/* Canvas */}
          <div className="relative flex-1 bg-white">
            <div ref={mountRef} onClick={(e) => { if (dim3dMode) handleDim3dPick(e); else onFaceClick(e); }} onPointerDown={(e) => (downPos.current = { x: e.clientX, y: e.clientY })} onPointerMove={(e) => { if (dim3dMode) onDim3dMove(e); }} className="w-full h-full" data-testid="three-canvas-container" style={{ backgroundImage: "radial-gradient(#E5E5E5 1px, transparent 1px)", backgroundSize: "20px 20px", cursor: (faceMode || dim3dMode) ? "crosshair" : "default" }} />
            {!hasModel && !loading && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="text-kann-text-sec text-sm">Voeg onderdelen toe om te beginnen</div></div>}
            {loading && <div className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="w-7 h-7 animate-spin text-kann-gold" /></div>}
            {hasModel && !faceMode && <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none"><Move3d className="w-3.5 h-3.5" /> Sleep om te draaien · scroll om te zoomen</div>}
            {hasModel && (
              <div className="absolute top-3 right-3 flex flex-wrap gap-1.5 justify-end items-center" style={{ maxWidth: "70%" }} data-testid="three-views">
                {[["Voor", "front"], ["Achter", "back"], ["Links", "left"], ["Rechts", "right"], ["Boven", "top"], ["Onder", "bottom"], ["Iso", "iso"]].map(([label, dir]) => (
                  <button key={dir} onClick={() => setView(dir)} data-testid={`three-view-${dir}`} className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${viewNormals[dir] ? "bg-kann-gold text-kann-ink" : "bg-black/70 hover:bg-kann-gold hover:text-kann-ink text-white"}`} title={viewNormals[dir] ? "Aangepast vlak gekoppeld" : ""}>{label}</button>
                ))}
                <button onClick={() => { setFaceMode((v) => !v); clearFace(); }} data-testid="three-face-mode" title="Selecteer een vlak en koppel het aan een aanzicht" className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors border ${faceMode ? "bg-kann-gold text-kann-ink border-kann-gold" : "bg-black/70 text-white border-transparent hover:bg-kann-gold hover:text-kann-ink"}`}><SquareDashedMousePointer className="w-3.5 h-3.5" /> Vlak</button>
                <button onClick={() => setView("iso")} data-testid="three-view-home" title="Iso-hoek" className="text-xs px-2 py-1 rounded-full bg-black/70 text-white hover:bg-kann-gold hover:text-kann-ink"><Home className="w-3.5 h-3.5" /></button>
                <button onClick={() => tiltView(engine.current, 22.5)} data-testid="three-tilt-up" title="Kantel omhoog" className="text-xs px-2 py-1 rounded-full bg-black/70 text-white hover:bg-kann-gold hover:text-kann-ink"><ChevronUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => tiltView(engine.current, -22.5)} data-testid="three-tilt-down" title="Kantel omlaag" className="text-xs px-2 py-1 rounded-full bg-black/70 text-white hover:bg-kann-gold hover:text-kann-ink"><ChevronDown className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {faceMode && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-kann-gold text-kann-ink text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 z-30" data-testid="three-face-hint">
                {frontNormal ? "Stap 2/2 — klik het ONDERVLAK (onderkant) aan" : "Stap 1/2 — klik het VOORVLAK aan"}
                <button onClick={() => { clearFace(); setFaceMode(false); }} data-testid="three-face-cancel" className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {dim3dMode && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-kann-ink text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 z-30" data-testid="three-dim3d-hint">
                {dim3dMode === "dia" ? "Klik op een rond vlak" : (dimP1.current ? (dimP2.current ? "Sleep de maatlijn en klik om te plaatsen" : "Klik het TWEEDE snappunt") : "Klik het EERSTE snappunt")}
                <button onClick={() => setDim3dMode(null)} data-testid="three-dim3d-cancel" className="hover:text-kann-gold"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {hasModel && dims3d.length > 0 && (
              <div className="absolute bottom-3 right-3 bg-white border border-kann-border/30 rounded-lg shadow-lg px-2 py-1.5 flex flex-wrap gap-1 max-w-[220px]" data-testid="three-dim3d-list">
                {dims3d.map((d, i) => (
                  <span key={d.id} className="text-[10px] px-1.5 py-0.5 rounded-full border border-kann-border/50 text-kann-text-sec flex items-center gap-1">
                    {({ aligned: "Afstand", x: "X", y: "Y", z: "Z", dia: "\u2300" }[d.kind] || "Maat")} {i + 1}
                    <button onClick={() => removeDim3d(d.id)} data-testid={`three-dim3d-del-${d.id}`} className="text-red-500 hover:text-red-600">×</button>
                  </span>
                ))}
              </div>
            )}
            <div className="absolute inset-0 pointer-events-none" data-testid="annotation-layer">
              {annotations.map((a) => (
                <div key={a.id} onPointerDown={(e) => startDrag(e, a.id)} data-testid={`annotation-${a.id}`} className="absolute pointer-events-auto cursor-move select-none" style={{ left: `${a.xr * 100}%`, top: `${a.yr * 100}%`, transform: "translate(-50%, -50%)" }}>
                  {a.type === "number" && <div className={`w-7 h-7 rounded-full bg-kann-ink text-white text-xs font-bold flex items-center justify-center ${selectedId === a.id ? "ring-2 ring-kann-gold" : ""}`} style={{ boxShadow: "0 0 0 2px rgba(219,150,13,0.5)" }}>{a.n}</div>}
                  {a.type === "text" && <div className={`px-2 py-0.5 bg-white/90 text-kann-ink text-xs font-semibold rounded whitespace-nowrap border border-kann-border/30 ${selectedId === a.id ? "ring-2 ring-kann-gold" : ""}`}>{a.text || "Tekst"}</div>}
                  {a.type === "icon" && <img src={iconUri(a.icon)} alt="" draggable={false} className={selectedId === a.id ? "ring-2 ring-kann-gold rounded" : ""} style={{ width: 36, height: 36, transform: `rotate(${a.rot || 0}deg)` }} />}
                </div>
              ))}
            </div>
            {iconPalette && hasModel && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white border border-kann-border/30 rounded-xl shadow-lg p-2 grid grid-cols-5 gap-1" data-testid="icon-palette">
                {iconKeys.map((key) => <button key={key} onClick={() => addIcon(key)} data-testid={`icon-${key}`} className="w-10 h-10 rounded-lg hover:bg-kann-surface-active flex items-center justify-center"><img src={iconUri(key)} alt={key} style={{ width: 22, height: 22 }} /></button>)}
              </div>
            )}
            {selected && (
              <div className="absolute bottom-3 left-3 bg-white border border-kann-border/30 rounded-lg shadow-lg px-3 py-2 flex items-center gap-2" data-testid="annotation-editor">
                {selected.type === "text" && <input value={selected.text} onChange={(e) => updateAnn(selected.id, { text: e.target.value })} data-testid="annotation-text-input" className="border border-kann-border/40 rounded px-2 py-1 text-sm w-44 outline-none focus:border-kann-gold" placeholder="Tekst…" />}
                {selected.type === "number" && <span className="text-xs text-kann-text-sec">Aanduiding {selected.n}</span>}
                {selected.type === "icon" && <button onClick={() => updateAnn(selected.id, { rot: ((selected.rot || 0) + 45) % 360 })} data-testid="annotation-rotate" className="text-kann-text-sec text-xs hover:text-kann-gold flex items-center gap-1"><RotateCw className="w-3.5 h-3.5" /> Draai 45°</button>}
                <button onClick={() => removeAnn(selected.id)} data-testid="annotation-delete" className="text-red-500 text-xs hover:underline">Verwijderen</button>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-kann-border/20 flex items-center gap-2 flex-wrap shrink-0">
          {hasModel && (
            <>
              <button onClick={() => setShowFaces((v) => !v)} data-testid="three-faces-toggle" className={`rounded-full px-3 py-2 text-sm flex items-center gap-2 border transition-colors ${showFaces ? "bg-kann-gold/15 border-kann-gold text-kann-text-pri" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}><Layers className="w-4 h-4" /> Grijze vlakken</button>
              <label className="flex items-center gap-2 text-xs text-kann-text-sec border border-kann-border/50 rounded-full px-3 py-1.5" title="Randgevoeligheid — hoger = geen lijnen op afrondingen/radii">Randen<input type="range" min="15" max="70" value={edgeAngle} onChange={(e) => setEdgeAngle(Number(e.target.value))} data-testid="three-edge-angle" className="w-20 accent-kann-gold" /></label>
              <button onClick={addNumber} data-testid="three-add-number" className="border border-kann-border/50 rounded-full px-3 py-2 text-sm text-kann-text-sec hover:bg-kann-surface-active transition-colors flex items-center gap-2"><Hash className="w-4 h-4" /> Nummer</button>
              <button onClick={addText} data-testid="three-add-text" className="border border-kann-border/50 rounded-full px-3 py-2 text-sm text-kann-text-sec hover:bg-kann-surface-active transition-colors flex items-center gap-2"><Type className="w-4 h-4" /> Tekst</button>
              <button onClick={() => setIconPalette((v) => !v)} data-testid="three-add-icon" className={`rounded-full px-3 py-2 text-sm flex items-center gap-2 border transition-colors ${iconPalette ? "bg-kann-gold/15 border-kann-gold text-kann-text-pri" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}><Shapes className="w-4 h-4" /> Icoon</button>
              <div className="flex items-center gap-1 border border-kann-border/50 rounded-full px-2 py-1" data-testid="three-dim3d-tools">
                <Ruler className="w-3.5 h-3.5 text-kann-gold mr-0.5" title="CAD-maten" />
                {[["aligned", "Afstand"], ["x", "X"], ["y", "Y"], ["z", "Z"], ["dia", "\u2300"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => startDim3d(k)} data-testid={`three-dim3d-${k}`} title={k === "dia" ? "Diameter van rond vlak" : (k === "aligned" ? "Rechte afstand tussen 2 snappunten" : `Maat langs ${k.toUpperCase()}-as`)}
                    className={`px-2 py-1 rounded-full text-[11px] transition-colors ${dim3dMode === k ? "bg-kann-ink text-white" : "text-kann-text-sec hover:bg-kann-surface-active"}`}>{lbl}</button>
                ))}
              </div>
            </>
          )}
          {error && <span className="text-xs text-red-500" data-testid="three-error">{error}</span>}
          <div className="ml-auto">
            <button onClick={capture} disabled={!hasModel} data-testid="three-capture-btn" className="bg-kann-ink text-white font-semibold rounded-full px-5 py-2 text-sm hover:bg-black transition-colors flex items-center gap-2 disabled:opacity-40"><Camera className="w-4 h-4" /> Vastleggen als tekening</button>
          </div>
        </div>
      </div>
    </div>
  );
}
